import { Box, Stack, useTheme } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SsoConnectorButton } from "src/components/SsoConnectorButton";
import { SsoLoginMode, SsoProtocol } from "src/pages/Authentication/ssoConnector";
import { controlWidth, Field } from "./SsoSettingsFields";

export type ProviderButtonValues = {
    selectedLogo: File | null;
    isLogoCleared: boolean;
};

type ProviderButtonFieldsProps = {
    label: string;
    loginMode: SsoLoginMode;
    onChange: (values: ProviderButtonValues) => void;
    protocol: SsoProtocol;
    providerName: string;
    storedLogo: string | undefined;
    values: ProviderButtonValues;
};

export const ProviderButtonFields = (props: ProviderButtonFieldsProps) => {
    const { label, loginMode, onChange, protocol, providerName, storedLogo, values } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const logoInput = useRef<HTMLInputElement>(null);
    const [pickedLogoUrl, setPickedLogoUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (values.selectedLogo === null) {
            setPickedLogoUrl(undefined);
            return;
        }

        const url = URL.createObjectURL(values.selectedLogo);
        setPickedLogoUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [values.selectedLogo]);

    const logoPreview = pickedLogoUrl ?? (values.isLogoCleared ? undefined : storedLogo);
    const previewLabel = label.trim() === "" ? providerName : label.trim();

    return (
        <Stack gap={theme.spacing(2)}>
            <Stack flexDirection="row" flexWrap="wrap" gap={theme.spacing(2)}>
                <Field label={t("settings.sso.connector_logo", "Logo")}>
                    <Stack gap={theme.spacing(1)} alignItems="flex-start">
                        {logoPreview !== undefined ?
                            <Box
                                component="img"
                                alt=""
                                src={logoPreview}
                                sx={{ height: theme.spacing(5), maxWidth: "100%" }}
                            />
                        :   <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                                {t("property.file_upload_info.no_file")}
                            </Typography>
                        }
                        <Stack flexDirection="row" gap={theme.spacing(1)} flexWrap="wrap">
                            <Button variant="secondary" onClick={() => logoInput.current?.click()}>
                                {t("settings.sso.choose_logo", "Choose a logo")}
                            </Button>
                            {logoPreview !== undefined && (
                                <Button
                                    variant="ternary"
                                    onClick={() => {
                                        onChange({ ...values, selectedLogo: null, isLogoCleared: true });
                                        if (logoInput.current !== null) logoInput.current.value = "";
                                    }}
                                >
                                    {t("delete")}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Field>
            </Stack>

            {providerName.trim() !== "" && (
                <Stack gap={theme.spacing(1)} alignItems="flex-start">
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2800] }}>
                        {t("settings.sso.preview", "Preview")}
                    </Typography>
                    <Box inert sx={{ ...controlWidth(theme) }}>
                        <SsoConnectorButton
                            connector={{
                                protocol,
                                providerName,
                                authorizationUrl: "",
                                label: previewLabel,
                                logo: logoPreview,
                            }}
                            label={
                                loginMode === "sso-first" ?
                                    t("login.sso.continue_with_connector", "Continue with {{connector}}", {
                                        connector: previewLabel,
                                    })
                                :   previewLabel
                            }
                            variant={loginMode === "sso-first" ? "primary" : "secondary"}
                            onClick={() => undefined}
                        />
                    </Box>
                </Stack>
            )}

            <Box
                component="input"
                ref={logoInput}
                type="file"
                accept="image/*"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onChange({
                        ...values,
                        selectedLogo: event.target.files?.[0] ?? null,
                        isLogoCleared: false,
                    })
                }
                sx={{ display: "none" }}
            />
        </Stack>
    );
};
